/**
 * Manual light/dark override, rendered top-right on every screen (`AppNav`,
 * `Landing`, `Privacy`). One button, not a segmented control, because there
 * are only two states and the icon already shows which one is active.
 *
 * Persists to `localStorage` and writes `data-theme` on `<html>`; `index.css`
 * defines `:root[data-theme='dark']` to win over `prefers-color-scheme` in
 * both directions. `index.html`'s inline script applies the stored choice
 * before first paint — keep the storage key and the two theme values in step
 * with it if either changes.
 */

import { useState } from 'react'
import { useI18n } from '../i18n'
import { Icon } from './Icon'
import './ThemeToggle.css'

type Theme = 'light' | 'dark'

const STORAGE_KEY = 'pivot-theme'

function readTheme(): Theme {
  const explicit = document.documentElement.getAttribute('data-theme')
  if (explicit === 'light' || explicit === 'dark') return explicit
  // jsdom (the test environment) has no `matchMedia` — fall back to light,
  // same as a browser whose `prefers-color-scheme` is unset.
  const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
  return prefersDark ? 'dark' : 'light'
}

export function ThemeToggle() {
  const { t } = useI18n()
  const [theme, setTheme] = useState<Theme>(readTheme)

  const toggle = () => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark'
    document.documentElement.setAttribute('data-theme', next)
    // `index.html`'s inline script only resolves `theme-color` at *load*
    // time — a live click here never re-runs it, so without this the phone
    // toolbar keeps the old colour until the next full reload, contradicting
    // the toggle the user just pressed (ticket §5's "must also update
    // theme-color when an override is stored", PIVOT-137). Read the resolved
    // `--paper` now that `data-theme` has flipped, rather than duplicating
    // the two literal hexes the inline script is forced to hardcode — this
    // runs after the stylesheet is loaded, so it can't drift from index.css
    // the way two independent literals could.
    const paper = getComputedStyle(document.documentElement).getPropertyValue('--paper').trim()
    if (paper) {
      document.querySelectorAll('meta[name="theme-color"]').forEach((meta) => {
        meta.setAttribute('content', paper)
      })
    }
    // Best-effort: a private-mode visitor keeps the toggle working for the
    // session even though the choice cannot survive a reload.
    try {
      window.localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // storage unavailable — nothing to reconcile, the in-memory state above
      // is still correct for the rest of this session.
    }
    setTheme(next)
  }

  return (
    <button
      type="button"
      className="theme-toggle"
      onClick={toggle}
      aria-label={t(theme === 'dark' ? 'a11y.themeToggleToLight' : 'a11y.themeToggleToDark')}
    >
      <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
    </button>
  )
}
